import {
  Building2,
  Coins,
  Database,
  FileClock,
  Gauge,
  Inbox,
  LayoutGrid,
  LogOut,
  Menu,
  Moon,
  Palette,
  Rocket,
  ScrollText,
  SlidersHorizontal,
  Code2,
  Sun,
  Users,
  Workflow,
  UserSearch,
  ArrowLeftRight,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../auth/auth";
import { useOrg } from "../api/org";
import { useCredits } from "../api/queries";
import { initials } from "../lib/format";
import { cx } from "./ui";

export function useTheme(): [string, () => void] {
  const [theme, setTheme] = useState(() => document.documentElement.dataset.theme || "light");
  const toggle = () => {
    const next = theme === "dark" ? "light" : "dark";
    if (next === "dark") document.documentElement.dataset.theme = "dark";
    else delete document.documentElement.dataset.theme;
    try {
      localStorage.setItem("solaros.theme", next);
    } catch {
      /* private mode */
    }
    setTheme(next);
  };
  return [theme, toggle];
}

export function Brand({ sub }: { sub?: string }) {
  return (
    <NavLink to="/" className="brand">
      <img src="/solaros-mark.png" alt="" />
      <div>
        <b>SolarOS</b>
        {sub && <small>{sub}</small>}
      </div>
    </NavLink>
  );
}

function Item({ to, icon, children, end }: { to: string; icon: ReactNode; children: ReactNode; end?: boolean }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => cx(isActive && "active")}>
      {icon}
      <span>{children}</span>
    </NavLink>
  );
}

function CreditPill({ orgId }: { orgId: string }) {
  const credits = useCredits(orgId, 1);
  const balance = credits.data?.balance;
  return (
    <NavLink to="/credits" className="credit-pill">
      <Coins size={18} />
      <div>
        <b>{balance ?? "—"}</b>
        <span> design credit{balance === 1 ? "" : "s"}</span>
      </div>
    </NavLink>
  );
}

function Shell({ admin }: { admin?: boolean }) {
  const { email, signOut } = useAuth();
  const { me, membership, memberships, selectOrg, isPlatformAdmin, orgId } = useOrg();
  const [theme, toggleTheme] = useTheme();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <div className={cx("shell", open && "nav-open")}>
      <aside className="sidebar" aria-label="Main navigation">
        <Brand sub={admin ? "Super admin" : undefined} />
        {!admin && membership && (
          <div className="org-switch">
            <div className="org-avatar">{initials(membership.organization_name)}</div>
            {memberships.length > 1 ? (
              <select value={membership.organization_id} onChange={(e) => selectOrg(e.target.value)} aria-label="Organisation">
                {memberships.map((m) => (
                  <option key={m.organization_id} value={m.organization_id}>
                    {m.organization_name}
                  </option>
                ))}
              </select>
            ) : (
              <div className="org-meta">
                <b>{membership.organization_name}</b>
                <span>{membership.role}</span>
              </div>
            )}
          </div>
        )}
        {admin ? (
          <nav className="nav">
            <div className="nav-section">Platform</div>
            <Item to="/admin/orgs" icon={<Building2 />}>Organisations</Item>
            <Item to="/admin/users" icon={<UserSearch />}>Users</Item>
            <Item to="/admin/trials" icon={<Inbox />}>Trial requests</Item>
            <div className="nav-section">Configuration</div>
            <Item to="/admin/settings" icon={<Gauge />}>Platform settings</Item>
            <Item to="/admin/stages" icon={<Workflow />}>Pipeline stages</Item>
            <Item to="/admin/cache" icon={<Database />}>Provider cache</Item>
            {orgId && (
              <>
                <div className="nav-section">Switch</div>
                <Item to="/designs" icon={<ArrowLeftRight />}>Company dashboard</Item>
              </>
            )}
          </nav>
        ) : (
          <nav className="nav">
            <div className="nav-section">Sales</div>
            <Item to="/get-started" icon={<Rocket />}>Get started</Item>
            <Item to="/designs" icon={<LayoutGrid />}>Designs &amp; leads</Item>
            <div className="nav-section">Website</div>
            <Item to="/widget" icon={<Code2 />}>Website widget</Item>
            <Item to="/branding" icon={<Palette />}>Branding</Item>
            <Item to="/rules" icon={<SlidersHorizontal />}>Rules</Item>
            <div className="nav-section">Account</div>
            <Item to="/credits" icon={<ScrollText />}>Credits &amp; billing</Item>
            <Item to="/team" icon={<Users />}>Team</Item>
            {isPlatformAdmin && (
              <>
                <div className="nav-section">Solar OS</div>
                <Item to="/admin/orgs" icon={<FileClock />}>Super admin</Item>
              </>
            )}
          </nav>
        )}
        <div className="sidebar-foot">
          {!admin && orgId && <CreditPill orgId={orgId} />}
          <div className="user-row">
            <span className="email" title={me.user.email}>{email || me.user.email}</span>
            <button className="icon-btn" onClick={toggleTheme} aria-label="Toggle dark mode" title="Toggle dark mode">
              {theme === "dark" ? <Sun /> : <Moon />}
            </button>
            <button className="icon-btn" onClick={() => signOut()} aria-label="Sign out" title="Sign out">
              <LogOut />
            </button>
          </div>
        </div>
      </aside>
      <div className="main">
        <header className="topbar">
          <button className="icon-btn" onClick={() => setOpen(true)} aria-label="Open menu">
            <Menu />
          </button>
          <Brand />
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function OrgLayout() {
  return <Shell />;
}

export function AdminLayout() {
  return <Shell admin />;
}
